import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import ReCAPTCHA from 'react-google-recaptcha';

import { HalfPageLayout } from 'bxcommon/components/HalfPageLayout/HalfPageLayout';
import LoginMessages from 'bxcommon/components/LoginMessages/LoginMessages';
import {
  BaseBox,
  BaseBoxHeading,
  BaseButtonFill,
  BaseCheckbox,
  BaseInput,
  BaseRadioGroup,
  InputWithIdField,
} from 'bxcommon';
import { passwordValidator, requiredStringValidator } from 'bxcommon/helpers/form.helper';
import { register } from 'bxcommon/misc/requests';
import { successToast } from 'bxcommon/store/slices/toaster.slice';
import errorsService, { REGISTRATION_ERRORS } from 'bxcommon/helpers/errors.helper';
import useRecaptcha from 'bxcommon/hooks/useRecaptcha';

import { AdvisorHeader } from '../AdvisorHeader/AdvisorHeader';

const RightContent = ({
  initialFormValues,
  onSubmit,
  validationSchema,
  titleOptions,
  recaptchaRef,
  handleRecaptcha,
  registerLoading,
  capchaToken,
}) => {
  const { t } = useTranslation();
  const { t: common } = useTranslation('common');

  return (
    <div>
      <div className="user-common-view__title">
        <h1 className="heading heading--primary heading--color-primary">{common('register.heading')}</h1>
        <div>{common('register.subheading')}</div>
      </div>
      <Formik initialValues={initialFormValues} onSubmit={onSubmit} validationSchema={validationSchema}>
        {(props) => (
          <BaseBox className="user-common-view__box" borderless>
            <BaseBoxHeading>{common('register.accountDetails')}</BaseBoxHeading>
            <Form className="form-container">
              <div className="row">
                <div className="col-1" />
                <div className="col-10">
                  <div className="row">
                    <Field
                      component={BaseRadioGroup}
                      id="registration-title"
                      name="title"
                      options={titleOptions}
                      data-testid="title"
                    />
                  </div>
                  <div className="row">
                    <InputWithIdField
                      name="first_name"
                      label={common('register.firstName')}
                      disabled={false}
                      id="registration-first_name"
                    />
                  </div>
                  <div className="row">
                    <InputWithIdField
                      name="last_name"
                      label={common('register.lastName')}
                      disabled={false}
                      id="registration-last_name"
                    />
                  </div>
                  <div className="row">
                    <InputWithIdField
                      name="email"
                      label={t('register.email')}
                      disabled={false}
                      id="registration-email"
                    />
                  </div>
                  <div className="row">
                    <Field
                      component={BaseInput}
                      name="password"
                      type="password"
                      label={common('register.password')}
                      inputHint={common('passwordHint')}
                      data-testid="password"
                      id="registration-password"
                      autoComplete="new-password"
                    />
                  </div>
                  <div className="row" data-testid="confirm-password-wrapper">
                    <Field
                      component={BaseInput}
                      name="confirm_password"
                      type="password"
                      label={common('register.confirmPassword')}
                      data-testid="confirm_password"
                      id="registration-confirm-password"
                      autoComplete="new-password"
                    />
                  </div>
                  <div className="row user-common-view__consent">
                    <Field component={BaseCheckbox} name="consent" checked={props.values.consent} data-testid="consent">
                      <Trans i18nKey="register.consent" ns="common">
                        <a
                          href="https://www.regservices.ch/?download=6313"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="link--underlined"
                        >
                          User Agreement
                        </a>
                        <a
                          href="https://www.regservices.ch/en/privacy-policy/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="link--underlined"
                        >
                          Privacy Policy
                        </a>
                      </Trans>
                    </Field>
                  </div>

                  {!window.top.__E2E__ && (
                    <div className="row captcha" data-testid="captcha">
                      <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={import.meta.env.VITE_CAPTCHA_KEY}
                        onChange={handleRecaptcha}
                      />
                    </div>
                  )}

                  <div className="row user-common-view__submit">
                    <div className="col-12">
                      <BaseButtonFill
                        icon="arrow-right"
                        iconRight
                        full
                        type="submit"
                        loading={registerLoading}
                        disabled={!capchaToken}
                        data-testid="register-button"
                      >
                        {common('register.submitButton')}
                      </BaseButtonFill>
                    </div>
                  </div>
                </div>
                <div className="col-1" />
              </div>
            </Form>
          </BaseBox>
        )}
      </Formik>
    </div>
  );
};

export const UserRegistration = () => {
  const { t } = useTranslation();
  const { t: common } = useTranslation('common');

  const navigate = useNavigate();

  const { capchaToken, recaptchaRef, handleRecaptcha } = useRecaptcha();
  const [registerLoading, setRegisterLoading] = useState(false);

  const initialFormValues = {
    title: '',
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirm_password: '',
    consent: false,
  };

  const validationSchema = Yup.object().shape({
    title: requiredStringValidator(),
    first_name: requiredStringValidator(),
    last_name: requiredStringValidator(),
    email: requiredStringValidator().email(),
    password: passwordValidator(),
    confirm_password: passwordValidator().oneOf([Yup.ref('password'), null], 'common:errors.passwordsDontMatch'),
    consent: Yup.boolean().oneOf([true], 'common:errors.requiredField'),
  });

  const onSubmit = async (formData) => {
    if (capchaToken) {
      formData.last_login = new Date();

      try {
        setRegisterLoading(true);
        await register(formData);

        successToast(common('register.success.successDescription'), common('register.success.successTitle'));
        setRegisterLoading(false);
        navigate('/login');
      } catch (error) {
        errorsService.handleErrorToast(error, REGISTRATION_ERRORS);
        setRegisterLoading(false);
      }
    }
  };

  const titleOptions = t(`register.titleOptions`, { returnObjects: true }).map((t) => ({ id: t, name: t }));

  return (
    <AdvisorHeader>
      <div className="user-common-view__content">
        <HalfPageLayout
          leftContent={<LoginMessages />}
          rightContent={
            <RightContent
              initialFormValues={initialFormValues}
              onSubmit={onSubmit}
              validationSchema={validationSchema}
              titleOptions={titleOptions}
              recaptchaRef={recaptchaRef}
              handleRecaptcha={handleRecaptcha}
              registerLoading={registerLoading}
              capchaToken={capchaToken}
            />
          }
        />
      </div>
    </AdvisorHeader>
  );
};

export default UserRegistration;
